import { MapNavigationService } from '../../core/map-navigation.service';
import { Injectable, NgZone } from '@angular/core';
import * as L from 'leaflet';
import { Subject } from 'rxjs';

import { MapShortcutService } from '../../core/map-shortcut.service';
import { isSelectableFeature, SelectableFeature } from '../../core/selectable-feature';
import { MapFeature } from '../../core/map-feature';
import { isPopoverFeature, PopoverFeature } from '../../core/popover-feature';

type MapInteractionMode = 'interact' | 'edit';

function isEditable(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  return !!element && (element.tagName === 'INPUT'
    || element.tagName === 'TEXTAREA'
    || element.isContentEditable);
}

/**
 * Sole owner of map input arbitration.
 *
 * It binds mouse and keyboard events, decides between selection, temporary
 * panning and edit mode, and dispatches selection gestures to map features.
 */
@Injectable({ providedIn: 'root' })
export class MapInteractionController {
  private map?: L.Map;
  private features: readonly MapFeature[] = [];
  private selectableFeatures: readonly SelectableFeature[] = [];
  private popoverFeatures: readonly PopoverFeature[] = [];
  private panHeld = false;
  private shiftHeld = false;
  private touchMultiple = false;
  private inputKind: 'mouse' | 'touch' = window.matchMedia('(pointer: coarse)').matches ? 'touch' : 'mouse';
  private readonly selectionApplied = new Subject<void>();
  readonly selectionApplied$ = this.selectionApplied.asObservable();
  private press?: { id: number; x: number; y: number; target: EventTarget | null };
  private pressTimer?: ReturnType<typeof setTimeout>;
  private ignoreTouchClickUntil = 0;
  private syntheticLongPress = false;
  private longPressConsumed = false;
  private readonly pointers = new Set<number>();
  get touchMultiSelect(): boolean { return this.touchMultiple; }

  get clearShortcutLabel(): string {
    const binding = this.shortcuts.binding('navigate');
    return binding === 'Escape' ? 'Esc' : binding.replace(/^(Key|Digit)/, '');
  }

  private mode: MapInteractionMode = 'interact';
  /** Prevents the browser click following a completed drag from replacing it. */
  private ignoreNextClick = false;

  private readonly backgroundClick = new Subject<void>();
  readonly backgroundClick$ = this.backgroundClick.asObservable();

  private selectionPointerDown = false;
  private selectionDragStarted = false;
  private startPt?: L.Point;
  private lastSelectionPoint?: L.Point;
  private selectionBox?: HTMLDivElement;

  constructor(
    private readonly shortcuts: MapShortcutService,
    private readonly navigation: MapNavigationService,
    private readonly zone?: NgZone,
  ) { }

  attach(map: L.Map): void {
    if (this.map === map) return;
    if (this.map) this.detach();
    this.map = map;
    map.on('mousedown', this.onDown, this);
    map.on('mousemove', this.onMove, this);
    map.on('mouseup', this.onUp, this);
    map.on('click', this.onMapClick, this);
    this.outsideAngular(() => {
      document.addEventListener('keydown', this.onKey);
      document.addEventListener('keyup', this.onKey);
      window.addEventListener('blur', this.onBlur);
      const container = map.getContainer();
      container.addEventListener('mousedown', this.onMouseDownCapture, true);
      container.addEventListener('click', this.onClickCapture, true);
      container.addEventListener('pointerdown', this.onPointerDown, true);
      container.addEventListener('contextmenu', this.onContextMenu);
      document.addEventListener('pointermove', this.onPointerMove, true);
      document.addEventListener('pointerup', this.onPointerEnd, true);
      document.addEventListener('pointercancel', this.onPointerEnd, true);
      document.addEventListener('mousemove', this.onMouseMoveCapture, true);
    });
    this.applyMapState();
  }

  setFeatures(features: readonly MapFeature[]): void {
    if (features.length === this.features.length
        && features.every(
          (feature, index) => feature === this.features[index],
        )) {
      // Counts/loading/visibility also emit the registry. Do not disturb an
      // editor's dragging state or an in-progress selection on those updates.
      this.syncPopovers();
      return;
    }
    this.cancelSelectionDrag();
    for (const feature of this.selectableFeatures) {
      feature.setObjectSelectionHandler?.(null);
      feature.disableSelection();
    }
    for (const feature of this.popoverFeatures) {
      feature.disablePopovers();
    }
    this.features = [...features];
    this.selectableFeatures = features.filter(isSelectableFeature);
    this.popoverFeatures = features.filter(isPopoverFeature);
    for (const feature of this.selectableFeatures) {
      feature.setObjectSelectionHandler?.((ids, originalEvent) =>
        this.applyObjectSelection(feature, ids, originalEvent));
    }
    if (this.mode === 'interact') {
      for (const feature of this.selectableFeatures) feature.enableSelection();
    }
    this.applyMapState();
  }

  beginEdit(): void {
    if (this.mode === 'edit') return;
    this.mode = 'edit';
    this.panHeld = false;
    this.shiftHeld = false;
    this.touchMultiple = false;
    this.cancelLongPress();
    this.cancelSelectionDrag();
    this.navigation.clearFocus();
    this.map?.closePopup();
    for (const feature of this.selectableFeatures) feature.disableSelection();
    this.applyMapState();
  }

  endEdit(): void {
    if (this.mode === 'interact') return;
    this.mode = 'interact';
    this.panHeld = false;
    for (const feature of this.selectableFeatures) feature.enableSelection();
    this.applyMapState();
  }

  /** Read-only feature details are blocked during modified selection or editing. */
  get canInspect(): boolean {
    return this.mode === 'interact' && !this.panHeld && !this.shiftHeld && !this.touchMultiple;
  }

  /** Build a map-bound editor without exposing Leaflet through navigation. */
  createSession<T>(factory: (map: L.Map) => T): T {
    if (!this.map) throw new Error('Map interaction surface is not attached.');
    return factory(this.map);
  }

  setContainerMode(...classes: string[]): void {
    if (!this.map) return;
    const container = this.map.getContainer();
    container.classList.remove('tr-mode-draw', 'tr-mode-edit');
    for (const className of classes) container.classList.add(className);
  }

  detach(): void {
    const map = this.map;
    if (!map) return;
    this.cancelSelectionDrag();
    for (const feature of this.selectableFeatures) {
      feature.setObjectSelectionHandler?.(null);
      feature.disableSelection();
    }
    for (const feature of this.popoverFeatures) {
      feature.disablePopovers();
    }
    this.navigation.clearFocus();
    this.features = [];
    this.popoverFeatures = [];
    this.selectableFeatures = [];
    this.mode = 'interact';
    this.panHeld = false;
    map.dragging.enable();
    map.boxZoom.disable();
    map.doubleClickZoom.disable();
    map.getContainer().style.cursor = '';
    map.getContainer().classList.remove('map-selecting');
    map.off('mousedown', this.onDown, this);
    map.off('mousemove', this.onMove, this);
    map.off('mouseup', this.onUp, this);
    map.off('click', this.onMapClick, this);
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('keyup', this.onKey);
    window.removeEventListener('blur', this.onBlur);
    const container = map.getContainer();
    container.removeEventListener('mousedown', this.onMouseDownCapture, true);
    container.removeEventListener('click', this.onClickCapture, true);
    container.removeEventListener('pointerdown', this.onPointerDown, true);
    container.removeEventListener('contextmenu', this.onContextMenu);
    document.removeEventListener('pointermove', this.onPointerMove, true);
    document.removeEventListener('pointerup', this.onPointerEnd, true);
    document.removeEventListener('pointercancel', this.onPointerEnd, true);
    document.removeEventListener('mousemove', this.onMouseMoveCapture, true);
    this.cancelLongPress();
    this.pointers.clear();
    this.shiftHeld = false;
    this.touchMultiple = false;
    this.map = undefined;
  }

  private setPanHeld(held: boolean): void {
    if (held === this.panHeld) return;
    this.panHeld = held;
    if (held) {
      this.cancelSelectionDrag();
    }
    this.applyMapState();
  }

  private syncPopovers(): void {
    const inspecting = this.canInspect;
    for (const feature of this.popoverFeatures) {
      if (inspecting && feature.enabled) feature.enablePopovers();
      else {
        feature.disablePopovers();
      }
    }
  }

  private applyMapState(): void {
    this.syncPopovers();
    const map = this.map;
    if (!map) return;
    const targets = this.targets();
    const selecting = this.mode === 'interact'
      && targets.length > 0
      && this.shiftHeld;
    const canPan = !selecting || this.panHeld;
    if (canPan) map.dragging.enable();
    else map.dragging.disable();

    // Leaflet reserves Shift-drag for box zoom, but Shift is our append-
    // selection modifier. Keep the
    // native handler off so a slightly dragged Shift-click cannot move the
    // viewport behind either a map object or the map background.
    map.boxZoom.disable();
    // Repeated object clicks must never move the viewport, in any mode.
    map.doubleClickZoom.disable();

    map.getContainer().classList.toggle('map-selecting', selecting);
    map.getContainer().style.cursor = !selecting
      ? ''
      : this.panHeld
        ? 'grab'
        : 'default';
  }

  private onDown = (event: L.LeafletMouseEvent) => {
    // A fresh press is a new interaction, even if the previous drag emitted no click.
    this.ignoreNextClick = false;
    if (this.mode === 'edit'
      || !this.selectableFeatures.length
      || this.panHeld
      || !event.originalEvent.shiftKey) return;
    this.selectionPointerDown = true;
    this.selectionDragStarted = false;
    this.startPt = event.containerPoint;
    this.lastSelectionPoint = event.containerPoint;
    this.outsideAngular(() =>
      document.addEventListener('mouseup', this.onDocumentUp, true));
  };

  private onMove = (event: L.LeafletMouseEvent) => {
    const map = this.map;
    if (!map) return;
    if (!this.selectionPointerDown || !this.startPt) return;
    this.lastSelectionPoint = event.containerPoint;
    if (!this.selectionDragStarted) {
      if (this.startPt.distanceTo(event.containerPoint) < 4) return;
      this.selectionDragStarted = true;
      this.selectionBox = L.DomUtil.create(
        'div', 'map-select-box', map.getContainer(),
      ) as HTMLDivElement;
    }
    if (this.selectionBox) {
      const point = event.containerPoint;
      this.selectionBox.style.left = `${Math.min(this.startPt.x, point.x)}px`;
      this.selectionBox.style.top = `${Math.min(this.startPt.y, point.y)}px`;
      this.selectionBox.style.width = `${Math.abs(point.x - this.startPt.x)}px`;
      this.selectionBox.style.height = `${Math.abs(point.y - this.startPt.y)}px`;
    }
  };

  private onUp = (event: L.LeafletMouseEvent) => {
    const map = this.map;
    if (!map || !this.selectionPointerDown) return;
    const { selectionDragStarted, startPt } = this;
    this.cancelSelectionDrag();
    if (!selectionDragStarted || !startPt) return;
    this.ignoreNextClick = true;
    const bounds = L.latLngBounds(
      map.containerPointToLatLng(startPt), event.latlng,
    );
    this.insideAngular(() => {
      this.navigation.clearFocus();
      for (const feature of this.targets()) feature.selectInBounds?.(bounds);
      this.selectionApplied.next();
    });
  };

  private onMapClick = (event: L.LeafletMouseEvent) => {
    if (this.mode !== 'interact' || this.panHeld) return;
    if (this.ignoreNextClick) {
      this.ignoreNextClick = false;
      return;
    }
    this.insideAngular(() => {
      for (const feature of this.targets()) {
        const ids = feature.objectIdsAt?.(event.latlng) ?? [];
        if (ids.length) {
          this.applyObjectSelection(feature, ids, event.originalEvent);
          return;
        }
      }
      if (event.originalEvent?.shiftKey || this.shiftHeld) return;
      this.touchMultiple = false;
      this.navigation.clearFocus();
      for (const feature of this.selectableFeatures) feature.clearSelection();
      this.backgroundClick.next();
      this.applyMapState();
      this.selectionApplied.next();
    });
  };

  private onDocumentUp = (event: MouseEvent) => {
    const map = this.map;
    if (!map || !this.selectionPointerDown || event.button !== 0) return;
    // Outside the map, finish the last visible preview, not an off-map extent.
    const point = map.getContainer().contains(event.target as Node)
      ? map.mouseEventToContainerPoint(event)
      : this.lastSelectionPoint;
    if (!point) return;
    this.onUp({
      containerPoint: point,
      latlng: map.containerPointToLatLng(point),
      originalEvent: event,
    } as L.LeafletMouseEvent);
  };

  private onBlur = () => {
    this.cancelSelectionDrag();
    this.cancelLongPress();
    this.pointers.clear();
    this.shiftHeld = false;
    this.setPanHeld(false);
    this.applyMapState();
  };

  private onKey = (event: KeyboardEvent) => {
    if (event.key === 'Shift' || event.code === 'ShiftLeft' || event.code === 'ShiftRight') {
      if (this.mode === 'edit') return;
      if (event.type === 'keyup' || !isEditable(event.target)) {
        this.shiftHeld = event.type === 'keydown';
        this.applyMapState();
      }
      return;
    }
    // Focus may have moved into an editor since the pan key was pressed.
    // Always release it before ignoring keyboard input from editable elements.
    if (event.type === 'keyup' && this.shortcuts.matches(event, 'temporaryPan')) {
      if (this.mode === 'edit') return;
      this.setPanHeld(false);
      return;
    }
    if (this.mode === 'edit' || event.repeat || event.defaultPrevented || isEditable(event.target)) return;
    if (this.shortcuts.matches(event, 'temporaryPan')) {
      event.preventDefault();
      this.setPanHeld(event.type === 'keydown');
      return;
    }
    if (event.type !== 'keydown') return;
    if (this.shortcuts.matches(event, 'navigate')) {
      this.navigation.clearFocus();
      for (const feature of this.selectableFeatures) feature.clearSelection();
      this.touchMultiple = false;
      this.cancelSelectionDrag();
      this.applyMapState();
      this.map?.closePopup();
    }
  };

  private applyObjectSelection(
    feature: SelectableFeature,
    ids: readonly (string | number)[],
    originalEvent?: MouseEvent,
  ): boolean {
    if (this.ignoreNextClick) {
      this.ignoreNextClick = false;
      return false;
    }
    if (this.mode !== 'interact' || this.panHeld || !feature.enabled
        || !this.selectableFeatures.includes(feature)) return false;

    this.insideAngular(() => {
      this.navigation.clearFocus();
      if (this.syntheticLongPress) {
        feature.appendObjectSelection?.(ids);
      } else if (originalEvent?.shiftKey || this.shiftHeld || this.touchMultiple) {
        feature.toggleObjectSelection?.(ids);
      } else {
        const matches = feature.matchesObjectSelection?.(ids)
          ?? (ids.length > 0 && feature.selection.count === ids.length
            && ids.every(id => feature.selection.has(id)));
        const deselect = matches && this.selectableFeatures.every(target =>
          target === feature || target.selection.count === 0);
        for (const target of this.selectableFeatures) target.clearSelection();
        if (!deselect) feature.appendObjectSelection?.(ids);
      }
      this.selectionApplied.next();
    });
    return true;
  }

  private readonly onMouseDownCapture = (event: MouseEvent) => {
    this.ignoreNextClick = false;
    if (!this.map || event.button !== 0 || !event.shiftKey || this.mode === 'edit'
      || this.isMapControl(event.target)) return;
    this.shiftHeld = true;
    this.applyMapState();
    event.preventDefault();
    event.stopPropagation();
    const point = this.map.mouseEventToContainerPoint(event);
    this.onDown({ containerPoint: point, latlng: this.map.containerPointToLatLng(point), originalEvent: event } as L.LeafletMouseEvent);
  };

  private readonly onMouseMoveCapture = (event: MouseEvent) => {
    if (!this.map || !this.selectionPointerDown || !this.map.getContainer().contains(event.target as Node)) return;
    const point = this.map.mouseEventToContainerPoint(event);
    this.onMove({ containerPoint: point, latlng: this.map.containerPointToLatLng(point), originalEvent: event } as L.LeafletMouseEvent);
  };

  private readonly onClickCapture = (event: MouseEvent) => {
    if (this.syntheticLongPress) return;
    if (this.ignoreNextClick || this.longPressConsumed || Date.now() < this.ignoreTouchClickUntil) {
      this.ignoreNextClick = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  private isMapControl(target: EventTarget | null): boolean {
    return target instanceof Element && !!target.closest('.leaflet-control, .leaflet-popup, button, input, textarea, a');
  }

  private readonly onPointerDown = (event: PointerEvent) => {
    if (this.isMapControl(event.target)) return;
    this.ignoreTouchClickUntil = 0;
    this.inputKind = event.pointerType === 'touch' ? 'touch' : 'mouse';
    if (this.inputKind === 'mouse' && this.touchMultiple) {
      this.touchMultiple = false;
      this.applyMapState();
    }
    if (event.pointerType !== 'touch' || this.mode === 'edit') return;
    this.pointers.add(event.pointerId);
    this.cancelLongPress();
    if (this.pointers.size !== 1) return;
    this.press = { id: event.pointerId, x: event.clientX, y: event.clientY, target: event.target };
    this.pressTimer = setTimeout(() => {
      const press = this.press;
      if (!press || !this.map) return;
      this.insideAngular(() => {
        this.touchMultiple = true;
        this.applyMapState();
        this.longPressConsumed = true;
        this.syntheticLongPress = true;
        (press.target as Element)?.dispatchEvent(new MouseEvent('click', {
          bubbles: true, cancelable: true, clientX: press.x, clientY: press.y,
        }));
        this.syntheticLongPress = false;
        this.ignoreTouchClickUntil = Date.now() + 1000;
      });
    }, 550);
  };

  private readonly onPointerMove = (event: PointerEvent) => {
    if (this.press?.id === event.pointerId
      && !this.longPressConsumed
      && Math.hypot(event.clientX - this.press.x, event.clientY - this.press.y) > 8) this.cancelLongPress();
  };

  private readonly onPointerEnd = (event: PointerEvent) => {
    this.pointers.delete(event.pointerId);
    if (this.press?.id === event.pointerId) {
      if (this.longPressConsumed) this.ignoreTouchClickUntil = Date.now() + 500;
      this.longPressConsumed = false;
      this.cancelLongPress();
    }
  };

  private readonly onContextMenu = (event: Event) => {
    if (this.inputKind === 'touch' && this.mode !== 'edit' && !this.isMapControl(event.target)) event.preventDefault();
  };

  private cancelLongPress(): void {
    if (this.pressTimer) clearTimeout(this.pressTimer);
    this.pressTimer = undefined;
    this.press = undefined;
    this.longPressConsumed = false;
  }

  private targets(): readonly SelectableFeature[] {
    return this.selectableFeatures.filter(feature => feature.enabled);
  }

  private cancelSelectionDrag(): void {
    document.removeEventListener('mouseup', this.onDocumentUp, true);
    this.lastSelectionPoint = undefined;
    this.selectionBox?.remove();
    this.selectionBox = undefined;
    this.selectionPointerDown = false;
    this.selectionDragStarted = false;
    this.startPt = undefined;

  }

  private insideAngular<T>(action: () => T): T {
    return this.zone ? this.zone.run(action) : action();
  }

  private outsideAngular<T>(action: () => T): T {
    return this.zone ? this.zone.runOutsideAngular(action) : action();
  }
}
